import * as React from 'react'

import { Check, ChevronRight } from 'lucide-react'
import { Controller } from 'react-hook-form'

import OptionsBody from '~/client/src/shared/components/OptionsList/pieces/OptionsBody'
import OptionsEmpty from '~/client/src/shared/components/OptionsList/pieces/OptionsEmpty'
import OptionsHeader from '~/client/src/shared/components/OptionsList/pieces/OptionsHeader'
import OptionsItem from '~/client/src/shared/components/OptionsList/pieces/OptionsItem'
import OptionsItemList from '~/client/src/shared/components/OptionsList/pieces/OptionsItemList'
import OptionsPanel from '~/client/src/shared/components/OptionsList/pieces/OptionsPanel'
import OptionsSearch from '~/client/src/shared/components/OptionsList/pieces/OptionsSearch'
import OptionsToolbar from '~/client/src/shared/components/OptionsList/pieces/OptionsToolbar'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { compareHierarchyGroupsByOptionOrder } from '~/client/src/shared/resources/grouping/hierarchyGrouping'
import {
  getHierarchyAccessors,
  IHierarchyAccessors,
  IHierarchyLeaf,
} from '~/client/src/shared/utils/hierarchyAccessors'

import FullScreenPickerSheet from '../../FullScreenPickerSheet/FullScreenPickerSheet'
import BaseStruxhubSelector from '../../StruxhubInputs/StruxhubSelector/BaseStruxhubSelector'
import { HierarchyFieldConfig, HierarchyFormOption } from '../types'
import { requiredFieldValidator } from '../validators'

interface IHierarchyFieldProps {
  field: HierarchyFieldConfig
  control: any
  error?: string
  hideHelperText?: boolean
}

interface IHierarchyPickerProps {
  title: string
  options: HierarchyFormOption[]
  selectedValue?: string | null
  initialPath: string[]
  onClose: () => void
  onSelect: (value: string | null) => void
}

interface ISearchResultItemProps {
  leaf: IHierarchyLeaf
  selected: boolean
  onClick: () => void
}

const seedPathForValue = (
  value: string | null,
  h: IHierarchyAccessors,
): string[] => {
  if (!value) return []
  const chain = h.getAncestorChain(value)
  if (!chain.length) return []
  return chain.slice(0, -1).map(node => node.value)
}

const SearchResultItem: React.FC<ISearchResultItemProps> = ({
  leaf,
  selected,
  onClick,
}) => {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected || undefined}
      onClick={onClick}
      className={`tw:flex tw:w-full tw:items-center tw:gap-2 tw:rounded-md tw:pl-1.5 tw:pr-2 tw:py-1 tw:text-left tw:outline-none tw:transition-colors tw:bg-transparent tw:border-0 tw:cursor-pointer tw:appearance-none tw:hover:bg-accent tw:hover:text-accent-foreground tw:focus-visible:bg-accent tw:focus-visible:text-accent-foreground ${
        selected ? 'tw:bg-accent tw:text-accent-foreground' : ''
      }`}
    >
      <span className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col">
        <span className="tw:truncate tw:text-app-dense">{leaf.label}</span>
        {leaf.pathLabel && (
          <span className="tw:truncate tw:text-app-dense-medium tw:text-muted-foreground">
            {leaf.pathLabel}
          </span>
        )}
      </span>
      {selected && (
        <Check className="tw:size-4 tw:shrink-0 tw:text-foreground" />
      )}
    </button>
  )
}

const HierarchyPicker: React.FC<IHierarchyPickerProps> = ({
  title,
  options,
  selectedValue,
  initialPath,
  onClose,
  onSelect,
}) => {
  const [path, setPath] = React.useState<string[]>(initialPath)
  const [searchTerm, setSearchTerm] = React.useState('')

  const h = React.useMemo(() => getHierarchyAccessors(options), [options])
  const leaves = React.useMemo(
    () =>
      h
        .getLeaves()
        .sort((a, b) =>
          compareHierarchyGroupsByOptionOrder(a.value, b.value, h),
        ),
    [h],
  )

  const normalizedSearchTerm = searchTerm.trim().toLowerCase()
  const isSearching = !!normalizedSearchTerm

  const currentParentValue = path[path.length - 1]
  const currentParentLabel = currentParentValue
    ? h.getOwnLabel(currentParentValue)
    : undefined
  const currentChildren = React.useMemo(
    () => h.getChildren(currentParentValue ?? null),
    [h, currentParentValue],
  )

  const searchResults = React.useMemo(
    () =>
      isSearching
        ? leaves.filter(
            leaf =>
              leaf.label.toLowerCase().includes(normalizedSearchTerm) ||
              leaf.pathLabel.toLowerCase().includes(normalizedSearchTerm) ||
              leaf.value.toLowerCase().includes(normalizedSearchTerm),
          )
        : [],
    [isSearching, leaves, normalizedSearchTerm],
  )

  const handleBack =
    !isSearching && path.length
      ? () => setPath(prev => prev.slice(0, -1))
      : undefined

  const handleSelect = (value: string) => {
    onSelect(value)
    onClose()
  }

  return (
    <OptionsPanel className="tw:flex-1 tw:p-2.5">
      <OptionsHeader
        title={isSearching ? title : currentParentLabel || title}
        onBack={handleBack}
        onClose={onClose}
      />
      <OptionsBody>
        <OptionsToolbar>
          <OptionsSearch
            value={searchTerm}
            autoFocus
            onChange={setSearchTerm}
          />
        </OptionsToolbar>
        <OptionsItemList>
          {isSearching ? (
            searchResults.length ? (
              searchResults.map(leaf => (
                <SearchResultItem
                  key={leaf.value}
                  leaf={leaf}
                  selected={leaf.value === selectedValue}
                  onClick={() => handleSelect(leaf.value)}
                />
              ))
            ) : (
              <OptionsEmpty />
            )
          ) : currentChildren.length ? (
            currentChildren.map(option =>
              h.hasChildren(option.value) ? (
                <OptionsItem
                  key={option.value}
                  label={option.label}
                  trailing={
                    <ChevronRight className="tw:size-4 tw:shrink-0 tw:text-muted-foreground" />
                  }
                  onClick={() => setPath(prev => [...prev, option.value])}
                />
              ) : (
                <OptionsItem
                  key={option.value}
                  label={option.label}
                  selected={option.value === selectedValue}
                  onClick={() => handleSelect(option.value)}
                />
              ),
            )
          ) : (
            <OptionsEmpty />
          )}
        </OptionsItemList>
      </OptionsBody>
    </OptionsPanel>
  )
}

const HierarchyField: React.FC<IHierarchyFieldProps> = ({
  field,
  control,
  error,
  hideHelperText,
}) => {
  const [isPickerOpen, setIsPickerOpen] = React.useState(false)
  const closePicker = () => setIsPickerOpen(false)
  const h = React.useMemo(
    () => getHierarchyAccessors(field.options),
    [field.options],
  )

  return (
    <Controller
      name={field.name}
      control={control}
      defaultValue={field.defaultValue ?? null}
      rules={{
        validate: field.required ? requiredFieldValidator : undefined,
      }}
      render={({ field: { onChange, value } }) => {
        const normalizedValue = value ? String(value) : null
        const displayValue = normalizedValue
          ? h.getPathLabel(normalizedValue)
          : ''

        return (
          <>
            <BaseStruxhubSelector
              label={field.label}
              value={displayValue}
              isRequired={field.required}
              isReadOnly={field.readOnly || field.displayOnly}
              isDisabled={field.disabled}
              isValid={!error}
              validationMessage={error}
              isHelperTextHidden={hideHelperText}
              selectorClassName="tw:h-10"
              onClick={() => setIsPickerOpen(true)}
              onValueReset={() => onChange(null)}
            >
              {displayValue && (
                <span className="tw:text-body-lg tw:text-foreground tw:truncate tw:leading-6">
                  {displayValue}
                </span>
              )}
            </BaseStruxhubSelector>
            <FullScreenPickerSheet isOpen={isPickerOpen} onClose={closePicker}>
              <HierarchyPicker
                key={normalizedValue ?? 'none'}
                title={Localization.translator.selectX(
                  field.label || field.name,
                )}
                options={field.options}
                selectedValue={normalizedValue}
                initialPath={seedPathForValue(normalizedValue, h)}
                onClose={closePicker}
                onSelect={onChange}
              />
            </FullScreenPickerSheet>
          </>
        )
      }}
    />
  )
}

export default HierarchyField
